// src/pages/CalculoHonorarios/index.js

import React, { useState, useEffect } from 'react';
import useHonorarios from './hooks/useHonorarios';
import ListagemHonorarios from './components/ListagemHonorarios';
import FormularioTrabalho from './components/FormularioTrabalho';
import DetalheCalculo from './components/DetalheCalculo';
import './CalculoHonorarios.css';

const CalculoHonorarios = () => {
  const {
    honorarios,
    loading,
    calculando,
    gerandoEmail,
    error,
    success,
    carregarHonorarios,
    //buscarHonorarioPorId,
    criarHonorario,
    atualizarHonorario,
    excluirHonorario,
    calcularHonorarios,
    gerarEmailProposta,
    buscarUltimoCalculo,
    clearMessages
  } = useHonorarios();

  const [telaAtual, setTelaAtual] = useState('listagem'); // listagem | formulario | detalhe
  const [honorarioEditando, setHonorarioEditando] = useState(null);
  const [honorarioDetalhe, setHonorarioDetalhe] = useState(null);
  const [calculoAtual, setCalculoAtual] = useState(null);

  // Carregar honorários ao montar
  useEffect(() => {
    carregarHonorarios();
  }, [carregarHonorarios]);

  // Limpar mensagens após 5 segundos
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        clearMessages();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success, clearMessages]);

  // Handler: Novo Honorário
  const handleNovoHonorario = () => {
    setHonorarioEditando(null);
    setTelaAtual('formulario');
    clearMessages();
  };

  // Handler: Editar Honorário
  const handleEditarHonorario = (honorario) => {
    setHonorarioEditando(honorario);
    setTelaAtual('formulario');
    clearMessages();
  };

  // Handler: Salvar (Criar ou Atualizar)
  const handleSalvarHonorario = async (dados) => {
    let resultado;

    if (honorarioEditando?.id) {
      // Atualizar
      resultado = await atualizarHonorario(honorarioEditando.id, dados);
    } else {
      // Criar
      resultado = await criarHonorario(dados);
    }

    if (resultado) {
      setTelaAtual('listagem');
      setHonorarioEditando(null);
    }
  };

  // Handler: Cancelar Formulário
  const handleCancelarFormulario = () => {
    setTelaAtual('listagem');
    setHonorarioEditando(null);
    clearMessages();
  };

  // Handler: Visualizar Cálculo
  const handleVisualizarCalculo = async (honorario) => {
    clearMessages();
    setHonorarioDetalhe(honorario);
    
    // Buscar último cálculo
    const calculo = await buscarUltimoCalculo(honorario.id);
    setCalculoAtual(calculo);
    
    setTelaAtual('detalhe');
  };

  // Handler: Calcular/Recalcular
  const handleCalcular = async (alteracoes = '') => {
    if (!honorarioDetalhe) return;

    clearMessages();

    // Se houver alterações, atualizar orientações adicionais
    if (alteracoes.trim()) {
      const dadosAtualizados = {
        ...honorarioDetalhe,
        orientacoes_adicionais: (honorarioDetalhe.orientacoes_adicionais || '') + 
          '\n\n--- ALTERAÇÕES SOLICITADAS ---\n' + alteracoes
      };

      await atualizarHonorario(honorarioDetalhe.id, dadosAtualizados);
      
      // Atualizar referência local
      setHonorarioDetalhe(dadosAtualizados);
    }

    // Executar cálculo
    const resultado = await calcularHonorarios(honorarioDetalhe.id);

    if (resultado) {
      setCalculoAtual({
        calculo: resultado.calculo,
        racional: resultado.racional,
        email: null,
        created_at: new Date().toISOString()
      });
    }
  };

  // Handler: Gerar Email
  const handleGerarEmail = async (alteracoes = null) => {
    if (!honorarioDetalhe) return;

    clearMessages();

    // ✅ NOVO: Passar alterações para o hook
    const resultado = await gerarEmailProposta(honorarioDetalhe.id, alteracoes);

    if (resultado) {
      setCalculoAtual(prev => ({
        ...prev,
        email: resultado.emailTexto
      }));
    }
  };


  // Handler: Voltar para Listagem
  const handleVoltar = () => {
    setTelaAtual('listagem');
    setHonorarioDetalhe(null);
    setCalculoAtual(null);
    clearMessages();
  };

  // Handler: Excluir
  const handleExcluir = async (id, descricao) => {
    const sucesso = await excluirHonorario(id, descricao);
    
    if (sucesso && telaAtual === 'detalhe' && honorarioDetalhe?.id === id) {
      handleVoltar();
    }
  };

  // Renderização condicional de telas
  const renderTela = () => {
    switch (telaAtual) {
      case 'formulario':
        return (
          <FormularioTrabalho
            honorarioEditando={honorarioEditando}
            onSalvar={handleSalvarHonorario}
            onCancelar={handleCancelarFormulario}
            loading={loading}
          />
        );

      case 'detalhe':
        return (
          <DetalheCalculo
            honorario={honorarioDetalhe}
            calculo={calculoAtual}
            onRecalcular={handleCalcular}
            onGerarEmail={handleGerarEmail}
            onVoltar={handleVoltar}
            calculando={calculando}
            gerandoEmail={gerandoEmail}
          />
        );

      case 'listagem':
      default:
        return (
          <ListagemHonorarios
            honorarios={honorarios}
            onVisualizar={handleVisualizarCalculo}
            onEditar={handleEditarHonorario}
            onExcluir={handleExcluir}
            loading={loading}
          />
        );
    }
  };

  return (
    <div className="calculo-honorarios-container">
      {/* Header */}
      <div className="calculo-honorarios-header">
        <h2>💰 Cálculo de Honorários</h2>
        <p>Cálculo automatizado de honorários profissionais com apoio de IA</p>
      </div>

      {/* Mensagens */}
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Ações (apenas na listagem) */}
      {telaAtual === 'listagem' && (
        <div className="calculo-honorarios-actions">
          <button 
            className="create-honorario-btn"
            onClick={handleNovoHonorario}
            disabled={loading}
          >
            ➕ NOVO CÁLCULO
          </button>

          <button 
            className="refresh-btn"
            onClick={carregarHonorarios}
            disabled={loading}
          >
            🔄 ATUALIZAR
          </button>
        </div>
      )}

      {/* Conteúdo Principal */}
      <div className="calculo-honorarios-content">
        {renderTela()}
      </div>
    </div>
  );
};

export default CalculoHonorarios;